import React from 'react'
import { Group, Text } from '@mantine/core'
import { IoFlash } from 'react-icons/io5'

export type TierName =
  | 'Newcomer'
  | 'Enthusiast'
  | 'Contributor'
  | 'Expert'
  | 'Curator'
  | 'Armorer';

const TIER_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  Newcomer: { bg: 'rgba(139,148,158,0.15)', color: '#8B949E', border: 'rgba(139,148,158,0.3)' },
  Enthusiast: { bg: 'rgba(88,166,255,0.15)', color: '#58A6FF', border: 'rgba(88,166,255,0.3)' },
  Contributor: { bg: 'rgba(63,185,80,0.15)', color: '#3FB950', border: 'rgba(63,185,80,0.3)' },
  Expert: { bg: 'rgba(232,93,4,0.2)', color: '#E85D04', border: 'rgba(232,93,4,0.4)' },
  Curator: { bg: 'rgba(201,168,76,0.2)', color: '#C9A84C', border: 'rgba(201,168,76,0.4)' },
  Armorer: {
    bg: 'linear-gradient(90deg, rgba(232,93,4,0.25), rgba(201,168,76,0.25))',
    color: '#C9A84C',
    border: 'rgba(201,168,76,0.5)'
  },
  Bronze: { bg: 'rgba(139,148,158,0.15)', color: '#8B949E', border: 'rgba(139,148,158,0.3)' },
  Silver: { bg: 'rgba(88,166,255,0.15)', color: '#58A6FF', border: 'rgba(88,166,255,0.3)' },
  Gold: { bg: 'rgba(232,93,4,0.2)', color: '#E85D04', border: 'rgba(232,93,4,0.4)' }
}

interface Props {
  tier: string;
  score: number;
  size?: 'sm' | 'md' | 'lg';
}

export default function AmmoScoreBadge ({ tier, score, size = 'md' }: Props) {
  const normalizedTier = tier in TIER_COLORS ? tier : tier || 'Newcomer'
  const colors = TIER_COLORS[normalizedTier] ?? TIER_COLORS.Newcomer

  if (size === 'lg') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          padding: '5px 12px',
          borderRadius: 5,
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontFamily: 'Oswald, sans-serif'
        }}
      >
        <IoFlash size={11} /> {normalizedTier} · <span style={{ color: '#E85D04', fontSize: 14, fontWeight: 900 }}>{score}</span> AmmoScore
      </span>
    )
  }

  if (size === 'sm') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          padding: '2px 6px',
          borderRadius: 4,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          fontFamily: 'Oswald, sans-serif'
        }}
      >
        <IoFlash size={9} /> {normalizedTier} · {score}
      </span>
    )
  }

  return (
    <Group spacing={6}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          padding: '3px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontFamily: 'Oswald, sans-serif'
        }}
      >
        <IoFlash size={10} /> {normalizedTier}
      </span>
      <Text size="sm" color="dimmed" weight={500}>
        {score}
      </Text>
    </Group>
  )
}
